import React, { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectItem } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { cn, formatCurrency } from '@/lib/utils';
import { toast } from 'sonner';
import {
  Plus,
  Loader2,
  Mail,
} from 'lucide-react';
import type { ApiResponse, Lead, LeadStage, Contact } from '@/types';

const STAGES: { key: LeadStage; label: string; color: string }[] = [
  { key: 'new', label: 'New', color: 'bg-blue-500' },
  { key: 'contacted', label: 'Contacted', color: 'bg-purple-500' },
  { key: 'quoted', label: 'Quoted', color: 'bg-amber-500' },
  { key: 'negotiation', label: 'Negotiation', color: 'bg-orange-500' },
  { key: 'won', label: 'Won', color: 'bg-emerald-500' },
  { key: 'lost', label: 'Lost', color: 'bg-red-500' },
];

const SOURCE_OPTIONS = [
  { value: 'referral', label: 'Referral' },
  { value: 'website', label: 'Website' },
  { value: 'social_media', label: 'Social Media' },
  { value: 'advertising', label: 'Advertising' },
  { value: 'direct', label: 'Direct Contact' },
  { value: 'other', label: 'Other' },
];

function LeadCard({ lead, onClick }: { lead: Lead; onClick: () => void }) {
  return (
    <div
      className="group cursor-pointer rounded-lg border bg-card p-3 shadow-sm transition-all hover:border-primary/50 hover:shadow-md"
      onClick={onClick}
      draggable
      onDragStart={(e) => {
        e.dataTransfer.setData('text/plain', JSON.stringify({ id: lead.id, stage: lead.stage }));
      }}
    >
      <div className="flex items-start justify-between">
        <div className="min-w-0 flex-1">
          <p className="font-medium text-sm truncate">
            {lead.contact?.name ?? 'Unnamed Lead'}
          </p>
          {lead.contact?.company && (
            <p className="text-xs text-muted-foreground truncate">{lead.contact.company}</p>
          )}
        </div>
        {lead.value != null && (
          <Badge variant="secondary" className="ml-2 shrink-0 text-xs">
            {formatCurrency(lead.value)}
          </Badge>
        )}
      </div>
      <div className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
        {lead.contact?.email && (
          <span className="flex items-center gap-1">
            <Mail className="h-3 w-3" /> {lead.contact.email}
          </span>
        )}
      </div>
      {lead.source && (
        <div className="mt-2">
          <Badge variant="outline" className="text-xs">
            {lead.source}
          </Badge>
        </div>
      )}
    </div>
  );
}

export function CRMPage(): React.ReactElement {
  const queryClient = useQueryClient();
  const [showAddLead, setShowAddLead] = useState(false);
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);

  // Add lead form state
  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [estimatedValue, setEstimatedValue] = useState('');
  const [source, setSource] = useState('');
  const [isAdding, setIsAdding] = useState(false);

  const { data: leads, isLoading } = useQuery({
    queryKey: ['leads'],
    queryFn: async (): Promise<Lead[]> => {
      const res: ApiResponse<Lead[]> = await api.get('/leads');
      return res.success ? (res.data ?? []) : [];
    },
  });

  const moveMutation = useMutation({
    mutationFn: async ({ id, stage }: { id: string; stage: LeadStage }) => {
      await api.patch(`/leads/${id}/stage`, { stage });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : 'Failed to move lead');
    },
  });

  const handleDrop = useCallback(
    (stage: LeadStage, e: React.DragEvent) => {
      e.preventDefault();
      try {
        const data = JSON.parse(e.dataTransfer.getData('text/plain')) as { id: string };
        moveMutation.mutate({ id: data.id, stage });
      } catch {
        // ignore invalid drops
      }
    },
    [moveMutation],
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
  }, []);

  const handleAddLead = async () => {
    if (!contactName.trim()) {
      toast.error('Contact name is required');
      return;
    }

    setIsAdding(true);
    try {
      await api.post('/leads', {
        contactName: contactName.trim(),
        contactEmail: contactEmail.trim() || undefined,
        contactPhone: contactPhone.trim() || undefined,
        source: source || undefined,
        estimatedValue: estimatedValue ? parseInt(estimatedValue, 10) * 100 : undefined,
      });
      toast.success('Lead created');
      setShowAddLead(false);
      setContactName('');
      setContactEmail('');
      setContactPhone('');
      setEstimatedValue('');
      setSource('');
      queryClient.invalidateQueries({ queryKey: ['leads'] });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to create lead');
    } finally {
      setIsAdding(false);
    }
  };

  const getLeadsByStage = (stage: LeadStage): Lead[] => {
    return (leads ?? []).filter((l) => l.stage === stage);
  };

  const stageCounts = STAGES.reduce((acc, stage) => {
    acc[stage.key] = getLeadsByStage(stage.key).length;
    return acc;
  }, {} as Record<LeadStage, number>);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">CRM Pipeline</h1>
          <p className="text-muted-foreground">Manage your leads and sales pipeline</p>
        </div>
        <Button onClick={() => setShowAddLead(true)}>
          <Plus className="mr-2 h-4 w-4" /> Add Lead
        </Button>
      </div>

      {/* Kanban Board */}
      {isLoading ? (
        <div className="grid gap-4" style={{ gridTemplateColumns: `repeat(${STAGES.length}, 1fr)` }}>
          {STAGES.map((stage) => (
            <Card key={stage.key}>
              <CardContent className="p-3">
                <Skeleton className="mb-2 h-6 w-20" />
                <Skeleton className="mb-2 h-16 w-full" />
                <Skeleton className="h-16 w-full" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className="grid gap-4 overflow-x-auto pb-4" style={{ gridTemplateColumns: `repeat(${STAGES.length}, minmax(280px, 1fr))` }}>
          {STAGES.map((stage) => {
            const stageLeads = getLeadsByStage(stage.key);

            return (
              <div
                key={stage.key}
                className="flex flex-col rounded-lg border bg-card/50"
                onDrop={(e) => handleDrop(stage.key, e)}
                onDragOver={handleDragOver}
              >
                <div className="flex items-center gap-2 border-b p-3">
                  <div className={cn('h-2 w-2 rounded-full', stage.color)} />
                  <span className="font-medium text-sm">{stage.label}</span>
                  <Badge variant="secondary" className="ml-auto text-xs">
                    {stageLeads.length}
                  </Badge>
                </div>
                <div className="flex-1 space-y-2 p-2 min-h-[200px]">
                  {stageLeads.map((lead) => (
                    <LeadCard
                      key={lead.id}
                      lead={lead}
                      onClick={() => setSelectedLead(lead)}
                    />
                  ))}
                  {stageLeads.length === 0 && (
                    <div className="flex h-24 items-center justify-center rounded-lg border border-dashed border-border text-xs text-muted-foreground">
                      Drop leads here
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add Lead Dialog */}
      <Dialog open={showAddLead} onOpenChange={setShowAddLead}>
        <DialogHeader>
          <DialogTitle>Add New Lead</DialogTitle>
          <DialogDescription>Enter the lead details below</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="contactName">Contact Name *</Label>
            <Input
              id="contactName"
              value={contactName}
              onChange={(e) => setContactName(e.target.value)}
              placeholder="John Smith"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="contactEmail">Email</Label>
            <Input
              id="contactEmail"
              type="email"
              value={contactEmail}
              onChange={(e) => setContactEmail(e.target.value)}
              placeholder="john@example.com"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="contactPhone">Phone</Label>
            <Input
              id="contactPhone"
              type="tel"
              value={contactPhone}
              onChange={(e) => setContactPhone(e.target.value)}
              placeholder="0412 345 678"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="source">Source</Label>
            <Select value={source} onValueChange={setSource} placeholder="Select source...">
              {SOURCE_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
              ))}
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="estimatedValue">Estimated Value ($)</Label>
            <Input
              id="estimatedValue"
              type="number"
              value={estimatedValue}
              onChange={(e) => setEstimatedValue(e.target.value)}
              placeholder="500000"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setShowAddLead(false)}>
            Cancel
          </Button>
          <Button onClick={handleAddLead} disabled={isAdding}>
            {isAdding ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Create Lead
          </Button>
        </DialogFooter>
      </Dialog>

      {/* Lead Detail Dialog */}
      <Dialog open={selectedLead !== null} onOpenChange={() => setSelectedLead(null)}>
        {selectedLead && (
          <>
            <DialogHeader>
              <DialogTitle>{selectedLead.contact?.name ?? 'Lead Details'}</DialogTitle>
              <DialogDescription>
                Created {new Date(selectedLead.createdAt).toLocaleDateString('en-AU')}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Stage</p>
                  <Badge>{selectedLead.stage}</Badge>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Value</p>
                  <p className="font-medium">
                    {selectedLead.value != null ? formatCurrency(selectedLead.value) : '—'}
                  </p>
                </div>
                {selectedLead.contact?.email && (
                  <div>
                    <p className="text-sm text-muted-foreground">Email</p>
                    <p className="text-sm">{selectedLead.contact.email}</p>
                  </div>
                )}
                {selectedLead.contact?.phone && (
                  <div>
                    <p className="text-sm text-muted-foreground">Phone</p>
                    <p className="text-sm">{selectedLead.contact.phone}</p>
                  </div>
                )}
                {selectedLead.source && (
                  <div>
                    <p className="text-sm text-muted-foreground">Source</p>
                    <p className="text-sm">{selectedLead.source}</p>
                  </div>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setSelectedLead(null)}>
                Close
              </Button>
            </DialogFooter>
          </>
        )}
      </Dialog>
    </div>
  );
}
